import { StorageService } from './storage';

/**
 * Biometric Authentication Service - WebAuthn API for Touch ID / Face ID
 */

interface BiometricCredential {
  id: string;
  publicKey: ArrayBuffer;
  createdAt: string;
}

class BiometricAuthService {
  private isEnabled = false;
  private isSupported = false;
  private credential: BiometricCredential | null = null;
  private isLocked = false;

  /**
   * Initialize biometric auth service
   */
  init(): boolean {
    // Check WebAuthn support
    if (!window.PublicKeyCredential) {
      console.warn('❌ WebAuthn not supported in this browser');
      return false;
    }

    this.isSupported = true;
    console.log('✅ WebAuthn supported');

    // Load saved credential
    const savedCredential = StorageService.get<any>('biometric_credential');
    if (savedCredential) {
      // Convert base64 back to ArrayBuffer if needed
      this.credential = {
        id: savedCredential.id,
        publicKey: typeof savedCredential.publicKey === 'string'
          ? this.base64ToArrayBuffer(savedCredential.publicKey)
          : savedCredential.publicKey,
        createdAt: savedCredential.createdAt,
      };
      this.isEnabled = true;
      console.log('✅ Biometric credential loaded from storage');
    }

    // Check if app should be locked
    const shouldLock = StorageService.get<boolean>('biometric_lock_on_start', false);
    if (this.isEnabled && shouldLock) {
      this.isLocked = true;
      console.log('🔒 App locked on start');
    }

    return true;
  }

  /**
   * Check if biometric auth is supported
   */
  isBiometricSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Check if biometric auth is enabled
   */
  isBiometricEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Check if app is currently locked
   */
  isAppLocked(): boolean {
    return this.isLocked;
  }

  /**
   * Register biometric credential
   */
  async register(): Promise<boolean> {
    if (!this.isSupported) {
      console.error('❌ WebAuthn not supported');
      throw new Error('WebAuthn not supported');
    }

    console.log('🔐 Starting biometric registration...');

    try {
      // Generate random challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      // Generate random user ID
      const userId = new Uint8Array(16);
      crypto.getRandomValues(userId);

      // Get RP ID (handle localhost)
      const hostname = window.location.hostname;
      const rpId = hostname === 'localhost' || hostname === '127.0.0.1'
        ? 'localhost'
        : hostname.replace(/:\d+$/, ''); // Remove port if present

      console.log('🔐 RP ID:', rpId);
      console.log('🔐 Full hostname:', window.location.hostname);
      console.log('🔐 User Agent:', navigator.userAgent);

      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'Edouard.ai',
          id: rpId,
        },
        user: {
          id: userId,
          name: 'user@edouard.ai',
          displayName: 'Edouard.ai User',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' as const }, // ES256
          { alg: -257, type: 'public-key' as const }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Touch ID / Face ID
          userVerification: 'required',
          requireResidentKey: false,
        },
        timeout: 60000,
        attestation: 'none',
      };

      console.log('🔐 Requesting credential creation...');
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      }) as PublicKeyCredential | null;

      if (!credential) {
        console.error('❌ No credential returned');
        return false;
      }

      console.log('✅ Credential created:', credential.id);

      // Save credential (store ID as string, not ArrayBuffer)
      const biometricCredential: BiometricCredential = {
        id: credential.id, // This is already a string
        publicKey: (credential.response as AuthenticatorAttestationResponse).attestationObject,
        createdAt: new Date().toISOString(),
      };

      StorageService.set('biometric_credential', {
        id: biometricCredential.id,
        // Convert ArrayBuffer to base64 for storage
        publicKey: this.arrayBufferToBase64(biometricCredential.publicKey),
        createdAt: biometricCredential.createdAt,
      });

      this.credential = biometricCredential;
      this.isEnabled = true;

      console.log('✅ Biometric registration successful');

      return true;
    } catch (error: any) {
      console.error('❌ Biometric registration error:', error);
      console.error('Error name:', error?.name);
      console.error('Error message:', error?.message);

      // Log specific WebAuthn errors
      if (error?.name === 'NotAllowedError') {
        console.error('User cancelled or denied permission');
      } else if (error?.name === 'InvalidStateError') {
        console.error('Authenticator already registered');
      } else if (error?.name === 'NotSupportedError') {
        console.error('Browser/device does not support this operation');
      }

      return false;
    }
  }

  /**
   * Authenticate with biometric
   */
  async authenticate(): Promise<boolean> {
    if (!this.isSupported || !this.credential) {
      console.error('❌ Cannot authenticate: not supported or no credential');
      return false;
    }

    console.log('🔐 Starting biometric authentication...');

    try {
      // Generate random challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      // Get RP ID (handle localhost)
      const hostname = window.location.hostname;
      const rpId = hostname === 'localhost' || hostname === '127.0.0.1'
        ? 'localhost'
        : hostname.replace(/:\d+$/, '');

      console.log('🔐 RP ID for auth:', rpId);

      // Convert credential ID to ArrayBuffer if it's a string
      let credentialIdBuffer: ArrayBuffer;
      if (typeof this.credential.id === 'string') {
        // The credential.id from WebAuthn is already a base64url string
        // We need to convert it to ArrayBuffer
        const encoder = new TextEncoder();
        credentialIdBuffer = encoder.encode(this.credential.id).buffer;
      } else {
        credentialIdBuffer = this.credential.id as any;
      }

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: rpId,
        allowCredentials: [{
          id: credentialIdBuffer,
          type: 'public-key' as const,
        }],
        userVerification: 'required',
        timeout: 60000,
      };

      console.log('🔐 Requesting authentication...');
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      });

      if (assertion) {
        // Authentication successful
        console.log('✅ Authentication successful');
        this.isLocked = false;
        return true;
      }

      console.error('❌ No assertion returned');
      return false;
    } catch (error: any) {
      console.error('❌ Biometric authentication error:', error);
      console.error('Error name:', error?.name);
      console.error('Error message:', error?.message);
      return false;
    }
  }

  /**
   * Disable biometric auth
   */
  disable() {
    StorageService.remove('biometric_credential');
    StorageService.remove('biometric_lock_on_start');
    this.credential = null;
    this.isEnabled = false;
    this.isLocked = false;
  }

  /**
   * Set lock on start preference
   */
  setLockOnStart(enabled: boolean) {
    StorageService.set('biometric_lock_on_start', enabled);
  }

  /**
   * Get lock on start preference
   */
  getLockOnStart(): boolean {
    return StorageService.get<boolean>('biometric_lock_on_start', false);
  }

  /**
   * Unlock app (for manual unlock fallback)
   * Also disables "lock on start" to prevent re-locking
   */
  unlock() {
    console.log('🔓 biometricAuth.unlock(): Unlocking app and disabling lock-on-start');
    this.isLocked = false;

    // Disable lock-on-start to prevent re-locking
    // User can re-enable it later in Settings if they want
    StorageService.set('biometric_lock_on_start', false);
    console.log('🔓 biometricAuth.unlock(): Lock-on-start disabled in localStorage');
  }

  /**
   * Helper: Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Helper: Convert base64 to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    try {
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    } catch (error) {
      // If base64 decode fails, assume it's already the credential ID
      const encoder = new TextEncoder();
      return encoder.encode(base64).buffer;
    }
  }
}

// Singleton instance
export const biometricAuth = new BiometricAuthService();
